import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import has from 'lodash.has';

import { Div, Text, Table } from '@pinpoint/design-kit';

import CONSTANTS from 'src/store/constants';
import { formatNumber } from 'src/functions';
import { ModelProp } from 'src/store/typings';

/*
 * There is a weird behavior where if we exceed 998 rows
 * the table rows starts to overlap on one another. Hence,
 * we try to max the number of rows to 998.
 * */
const MAX_ROWS = 998;

// eslint-disable-next-line react/prop-types
function Validation({ value = null }) {
  if ([null, ''].includes(value)) return null;
  return (
    <Div
      tag="span"
      w="1rem"
      h="1rem"
      bg={value ? 'tableauOrange' : 'tableauBlue'}
      rounded="circle"
      data-tip={`${value ? '' : 'Non-'}Claimant`}
    />
  );
}

const dashOrFixed = ({ value }) => ([null, undefined].includes(value) ? '-' : value.toFixed(1));

export default function ActuarialMode({ modelView: { model } }) {
  const isLegacy = ['LIFT_VERSION_1_ACT', null, undefined].includes(model?.payload?.version);
  const talScoresSampled = useMemo(
    () =>
      get(
        model,
        'modelData.dataPayload.tal_scores_sampled',
        get(model, 'modelData.dataPayload.talScoresSampled', []).slice(0, 50)
      ),
    [model]
  );
  const hasPremiumPerc = useMemo(
    () => has(talScoresSampled, '[0].premiums_percentile') || has(talScoresSampled, '[0].premium_percentile'),
    [talScoresSampled]
  );
  const hasPremiumCumSum = useMemo(() => has(talScoresSampled, '[0].premium_cumsum'), [talScoresSampled]);
  const hasMatchType = useMemo(
    () => has(talScoresSampled, '[0].match_type') || has(talScoresSampled, '[0].matchType'),
    [talScoresSampled]
  );
  const predictionTruncate = model.dataModelType === CONSTANTS.TAL_MODEL.TAG.ACT_FREQUENCY ? 10 : 2;
  const internalValidationPercentage = useMemo(
    () =>
      model?.lifecycle_metadata?.APPROVED?.internal_validation_percentage ??
      model?.lifecycle_metadata?.APPROVED?.internalValidationPercentage ??
      model?.lifecycleMetadata?.APPROVED?.internal_validation_percentage ??
      model?.lifecycleMetadata?.APPROVED?.internalValidationPercentage ??
      0,
    [model]
  );

  const numberSort = useMemo(
    () => (rowA, rowB, columnId) => {
      const a = Number(rowA.values[columnId]);
      const b = Number(rowB.values[columnId]);
      return a > b ? 1 : -1;
    },
    []
  );

  const columns = useMemo(() => {
    const defaultCols = [
      {
        id: 'pin',
        Header: <Text data-test="crm-header">CRM ID</Text>,
        justify: 'left',
        padding: { l: '1rem' },
        cellPadding: { l: '1rem' },
        cellJustify: 'left',
        accessor: 'CRM_ID',
        disableSortBy: true,
      },
      {
        id: 'talScore',
        Header: <Text data-test="score-header">Prediction Value</Text>,
        cellJustify: 'center',
        sortType: numberSort,
        accessor: (row) => get(row, 'prediction', get(row, 'probability', get(row, 'prediction_value'))),
        Cell: ({ value }) => {
          if (value === null) return value;
          return predictionTruncate === 2
            ? `$${formatNumber(parseFloat(value).toFixed(predictionTruncate))}`
            : parseFloat(value).toFixed(predictionTruncate);
        },
      },
    ];
    if (isLegacy) {
      defaultCols.push({
        id: 'percentile',
        Header: (
          <Text textTransform="none" data-test="prem-perc-header">
            Percentage of Premiums
          </Text>
        ),
        cellJustify: 'center',
        sortType: numberSort,
        accessor: (row) => Number(get(row, 'percentile', get(row, 'prediction_percentile'))),
        Cell: dashOrFixed,
      });
    } else {
      defaultCols.push({
        id: 'person_percentile',
        Header: (
          <Text textTransform="none" data-test="pers-perc-header">
            Percentile by Persons
          </Text>
        ),
        cellJustify: 'center',
        sortType: numberSort,
        accessor: (row) =>
          Number(
            get(row, 'person_percentile', get(row, 'person_prediction_percentile', get(row, 'percentile_persons')))
          ),
        Cell: dashOrFixed,
      });
      if (hasPremiumCumSum)
        defaultCols.push({
          id: 'premiumCumSum',
          Header: <Text data-test="score-header">Cumulative Sum of Premiums</Text>,
          cellJustify: 'center',
          sortType: numberSort,
          accessor: (row) => get(row, 'premium_cumsum'),
          Cell: ({ value }) => {
            if (value === null) return value;
            return `$${formatNumber(parseFloat(value).toFixed(2))}`;
          },
        });
      if (hasPremiumPerc)
        defaultCols.push({
          id: 'premium_percentile',
          Header: (
            <Text textTransform="none" data-test="prem-perc-header">
              Percentage of Premiums
            </Text>
          ),
          cellJustify: 'center',
          sortType: numberSort,
          accessor: (row) =>
            Number(
              row?.premium_percentile ??
                row?.premiums_percentile ??
                row?.premium_prediction_percentile ??
                row?.percentile_premiums
            ),
          Cell: dashOrFixed,
        });
    }
    if (hasMatchType)
      defaultCols.splice(1, 0, {
        id: 'matchType',
        Header: <Text data-test="match-type-header">Match Type</Text>,
        cellJustify: 'center',
        sortType: 'alphanumeric',
        accessor: (row) => get(row, 'matchType', get(row, 'match_type', '-')),
      });
    if (internalValidationPercentage > 0)
      defaultCols.push({
        id: 'validation',
        Header: <Text data-test="val-header">Claimant Status</Text>,
        cellJustify: 'center',
        sortType: 'alphanumeric',
        accessor: (entry) => get(entry, 'validation', 0),
        Cell: Validation,
      });
    return defaultCols;
  }, [
    hasPremiumPerc,
    hasMatchType,
    hasPremiumCumSum,
    predictionTruncate,
    isLegacy,
    numberSort,
    internalValidationPercentage,
  ]);

  const colsLenStr = '1fr '.repeat(columns.length - 1);
  return (
    <>
      <Text m={{ t: '1rem', l: '2rem', b: '1.25rem' }}>Sample of records from validation set:</Text>
      <Div m={{ x: '1rem', b: '1rem' }} overflow="scroll" flexGrow="1">
        <Table
          h="10vh" // the height will still overflow, it just needs to be an absolute value with overflow undefined
          overflow={undefined}
          columns={columns}
          data={talScoresSampled.slice(0, MAX_ROWS)}
          gridState={{ sortBy: [{ id: 'talScore', desc: true }] }}
          m="0"
          p="0"
          widths={'1fr '.concat(colsLenStr)}
        />
      </Div>
    </>
  );
}

ActuarialMode.propTypes = {
  modelView: PropTypes.shape({
    model: ModelProp.isRequired,
    downloadUrl: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    fetchModelStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
  }).isRequired,
};
